'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { useDevice } from '@/contexts/DeviceContext';
import { RaffleExtractionType } from '@/types/enums/HighlightedRaffle';
import type { HighlightedRaffleDetails } from '@/types/models';

type MainPrize = NonNullable<HighlightedRaffleDetails['mainPrizes']>[0];

type UsePrizesParams = {
  prizes: MainPrize[];
  extractionType?: string;
};

type UsePrizesReturn = {
  currentPrize: MainPrize | null;
  currentIndex: number;
  totalPrizes: number;
  showNavigation: boolean;
  canGoNext: boolean;
  canGoPrev: boolean;
  goToNext: () => void;
  goToPrev: () => void;
  goToIndex: (index: number) => void;
  showExtractionMethod: boolean;
  isMobile: boolean;
};

export const usePrizes = ({
  prizes,
  extractionType,
}: UsePrizesParams): UsePrizesReturn => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { isMobile } = useDevice();

  const totalPrizes = useMemo(() => prizes?.length ?? 0, [prizes]);

  const currentPrize = useMemo(
    () => prizes?.[currentIndex] ?? null,
    [prizes, currentIndex],
  );

  const showNavigation = useMemo(() => totalPrizes > 1, [totalPrizes]);

  const canGoNext = useMemo(
    () => currentIndex < totalPrizes - 1,
    [currentIndex, totalPrizes],
  );

  const canGoPrev = useMemo(() => currentIndex > 0, [currentIndex]);

  const showExtractionMethod = useMemo(
    () =>
      Boolean(
        extractionType === RaffleExtractionType.LOTTERY &&
        currentPrize?.sequenceRule?.length,
      ),
    [extractionType, currentPrize],
  );

  const goToNext = useCallback(() => {
    if (canGoNext) {
      setCurrentIndex((prev) => prev + 1);
    }
  }, [canGoNext]);

  const goToPrev = useCallback(() => {
    if (canGoPrev) {
      setCurrentIndex((prev) => prev - 1);
    }
  }, [canGoPrev]);

  const goToIndex = useCallback(
    (index: number) => {
      if (index >= 0 && index < totalPrizes) {
        setCurrentIndex(index);
      }
    },
    [totalPrizes],
  );

  useEffect(() => {
    setCurrentIndex(0);
  }, [totalPrizes]);

  return {
    currentPrize,
    currentIndex,
    totalPrizes,
    showNavigation,
    canGoNext,
    canGoPrev,
    goToNext,
    goToPrev,
    goToIndex,
    showExtractionMethod,
    isMobile,
  };
};
